import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface QuantityStepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  /** Read out with the buttons, e.g. "Masala Dosa". */
  label: string;
  className?: string;
}

/**
 * − 2 +. Stepping below `min` calls `onChange(min - 1)` only when `min` is 1,
 * which is how a cart row removes itself; the caller decides what 0 means.
 */
export function QuantityStepper({
  value,
  onChange,
  min = 1,
  max = 99,
  disabled,
  label,
  className,
}: QuantityStepperProps) {
  return (
    <div
      className={cn('bg-muted inline-flex h-9 items-center rounded-full', className)}
      role="group"
      aria-label={`Quantity of ${label}`}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="rounded-full"
        aria-label={`Decrease ${label}`}
        disabled={disabled || value <= min - 1}
        onClick={() => onChange(Math.max(min - 1, value - 1))}
      >
        <Minus />
      </Button>
      <span className="w-7 text-center text-sm font-semibold tabular-nums" aria-live="polite">
        {value}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="rounded-full"
        aria-label={`Increase ${label}`}
        disabled={disabled || value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        <Plus />
      </Button>
    </div>
  );
}
